import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getJobDetailsUrl } from '../../../utils/urlHelpers';
import { formatHourlySalaryRange } from '../../../utils/currencyUtils';
import { useAuth } from '../../../hooks/useAuth';
import Icon from '../../../components/AppIcon';
import ImageWithFallback from '../../../components/ImageWithFallback';

const SimilarJobs = ({ jobs }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const formatSalary = (hourlyMin, hourlyMax, jobType, jobCurrency = 'USD') => {
    // Get user's preferred currency from profile (fallback to USD)
    const userCurrency = userProfile?.preferred_currency || 'USD';
    
    return formatHourlySalaryRange({
      hourlyMin,
      hourlyMax,
      jobType,
      jobCurrency,
      userCurrency,
      showConversionNote: false // Don't show conversion note in similar jobs to save space
    }) || 'Not specified';
  };

  if (!jobs || jobs.length === 0) return null;

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center">
        <Icon name="Briefcase" size={18} className="text-primary mr-2" />
        Similar Jobs
      </h3>

      <div className="space-y-4">
        {jobs.slice(0, 3).map((job) => (
          <div
            key={job.id}
            onClick={() => navigate(getJobDetailsUrl(job))}
            className="p-4 border border-border rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
          >
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-secondary-100 flex-shrink-0">
                <ImageWithFallback 
                  src={job?.employer?.profile_picture || job?.company_logo} 
                  fallbackSrc="/assets/images/Logo/webp/VAS-LOGO.webp"
                  alt={job?.company_name || 'Company logo'}
                  className="w-full h-full object-cover"
                  lazy={true}
                />
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-text-primary text-sm line-clamp-1 hover:text-primary transition-colors">
                  {job?.title}
                </h4>
                <p className="text-xs text-text-secondary mb-1">
                  {job?.company_name}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1">
                    <Icon name="MapPin" size={12} className="text-text-muted" />
                    <span className="text-xs text-text-muted">{job?.location}</span>
                  </div>
                  
                  <div className="text-xs text-text-secondary">
                    {job?.created_at 
                      ? formatDistanceToNow(new Date(job.created_at), { addSuffix: true })
                      : 'Recently'
                    }
                  </div>
                </div>

                <div className="mt-2">
                  <span className="text-xs font-medium text-success">
                    {formatSalary(job?.budget_min, job?.budget_max, job?.job_type, job?.currency)}
                  </span>
                </div>

                {job?.skills_required && job.skills_required.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {job.skills_required.slice(0, 2).map((skill, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded"
                      >
                        {skill}
                      </span>
                    ))}
                    {job.skills_required.length > 2 && (
                      <span className="text-xs text-text-muted">
                        +{job.skills_required.length - 2}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-border">
        <button
          onClick={() => navigate('/job-search')}
          className="w-full text-center text-primary hover:text-primary-600 text-sm font-medium transition-colors"
        >
          View all jobs →
        </button>
      </div>
    </div>
  );
};

export default SimilarJobs;
